import {useDeepMemo} from '@wandb/weave/hookUtils';
import {useCallback, useMemo, useRef} from 'react';

import {
  RunSelectorRunsQueryVariables,
  RunSelectorRunsWithIdQueryVariables,
  useRunSelectorRunsQuery,
  useRunSelectorRunsWithIdQuery,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import * as Filters from '../../../util/filters';
import {isNetworkRequestSettled} from '../../../util/isNetworkRequestSettled';
import * as Run from '../../../util/runs';
import {RunSelectorRunsPartialVars} from '../types';

type DeltaVariables = Pick<
  RunSelectorRunsQueryVariables | RunSelectorRunsWithIdQueryVariables,
  'currentRuns' | 'lastUpdated'
>;

export type UseRunSelectorRunsVariablesArgs = {
  /** Determines how many runs will be returned on initial load and when loading more runs */
  limit: number;
  /** Variables that will be used to compute the full set of query variables */
  partialVariables: RunSelectorRunsPartialVars;
  /** If true, filters will be stricter so only selected (visualized) runs are returned */
  shouldOnlyShowSelected?: boolean;
  internalId: string | undefined;
};

export type UseRunSelectorRunsQueryArgs = {
  variables:
    | RunSelectorRunsQueryVariables
    | RunSelectorRunsWithIdQueryVariables;
  /** If true, will not query for runs */
  skip?: boolean;
  deltaVariablesRef: React.MutableRefObject<DeltaVariables>;
};

export const useVariables = ({
  limit,
  partialVariables,
  shouldOnlyShowSelected,
  internalId,
}: UseRunSelectorRunsVariablesArgs) => {
  // Track these values so the backend knows what to compute deltas against.
  // Must use ref because we don't want to trigger a new request every time
  // these values are updated; instead, we just want to make sure they're
  // correctly populated when a request is made.
  const deltaVariablesRef = useRef<DeltaVariables>({
    currentRuns: [],
    lastUpdated: new Date(0).toISOString(),
  });

  const {
    filters: baseFilters,
    selections,
    grouping,
    ...passThroughVars
  } = partialVariables;

  // passThroughVars needs to be memoized by value because it is recreated on every render
  // due to the spread operator
  const passThroughVarsMemoized = useDeepMemo(passThroughVars);

  // hack! baseFilters is changing on eyeball click due to a recent cache change.
  // this is a temporary fix to prevent the erroneous shimmer when baseFilters are
  // value-equal but erroneously not ref equal. TODO: remove once cache change
  // is fixed.
  const baseFiltersMemoized = useDeepMemo(baseFilters);

  const filters = useMemo(() => {
    return shouldOnlyShowSelected && selections != null
      ? Filters.And([baseFiltersMemoized, selections])
      : baseFiltersMemoized;
  }, [shouldOnlyShowSelected, selections, baseFiltersMemoized]);

  const variablesWithEntityAndProjectName = useMemo(() => {
    const groupKeys = grouping?.map(Run.keyToServerPath) ?? [];
    const variables: RunSelectorRunsQueryVariables = {
      // delta vars
      currentRuns: deltaVariablesRef.current.currentRuns,
      // fresh lastUpdated time ensures correct results on query change
      // it should only be set to something else on polled requests
      lastUpdated: new Date(0).toISOString(),
      // logical vars
      ...passThroughVarsMemoized,
      filters: JSON.stringify(Filters.toMongo(filters)),
      groupKeys,
      limit,
    };
    return variables;
  }, [limit, filters, passThroughVarsMemoized, grouping, deltaVariablesRef]);

  const {entityName, projectName, ...variablesWithoutInternalId} =
    variablesWithEntityAndProjectName;

  const variablesWithoutInternalIdMemoized = useDeepMemo(
    variablesWithoutInternalId
  );

  const variablesWithInternalId = useMemo(() => {
    if (internalId == null) {
      return null;
    }
    return {
      ...variablesWithoutInternalIdMemoized,
      internalId: internalId,
    };
  }, [variablesWithoutInternalIdMemoized, internalId]);

  const variables =
    variablesWithInternalId ?? variablesWithEntityAndProjectName;
  return {variables, deltaVariablesRef};
};

function hasInternalId(
  variables: RunSelectorRunsQueryVariables | RunSelectorRunsWithIdQueryVariables
): variables is RunSelectorRunsWithIdQueryVariables {
  return (variables as RunSelectorRunsWithIdQueryVariables).internalId != null;
}
export const useRunSelectorRunsDataQuery = ({
  variables,
  skip,
  deltaVariablesRef,
}: UseRunSelectorRunsQueryArgs) => {
  const isInternalIdQuery = hasInternalId(variables);
  const runSelectorRuns = useRunSelectorRunsQuery({
    context: propagateErrorsContext(),
    fetchPolicy: 'no-cache',
    skip: skip || isInternalIdQuery,
    variables: variables as RunSelectorRunsQueryVariables,
  });

  const runSelectorRunsWithId = useRunSelectorRunsWithIdQuery({
    context: propagateErrorsContext(),
    fetchPolicy: 'no-cache',
    skip: skip || !isInternalIdQuery,
    variables: variables as RunSelectorRunsWithIdQueryVariables,
  });
  const queryResults = isInternalIdQuery
    ? runSelectorRunsWithId
    : runSelectorRuns;

  const refetch = useCallback(() => {
    if (isInternalIdQuery) {
      return runSelectorRunsWithId.refetch({
        ...runSelectorRunsWithId.variables,
        ...deltaVariablesRef.current,
      });
    }
    return runSelectorRuns.refetch({
      ...runSelectorRuns.variables,
      ...deltaVariablesRef.current,
    });
  }, [
    deltaVariablesRef,
    isInternalIdQuery,
    runSelectorRuns,
    runSelectorRunsWithId,
  ]);

  const isLoadingRuns =
    !skip && !isNetworkRequestSettled(queryResults.networkStatus);
  return {
    data: queryResults.data,
    isLoadingRuns,
    refetch,
  };
};
